import { useEffect, useMemo, useState } from 'react';

import {
  VERIFICATION_CHANNEL,
  VerificationChannel,
} from '../../../constants/verificationChannel';
import { twoFAService } from '../../../services/twoFAService';

type Mode = 'verifyCode' | 'chooseVerificationMethod';

type UseTwoFAAuthProps = {
  phoneNumber: string;
};

type UseTwoFAAuthReturn = {
  mode: Mode;
  consumer: string;
  verificationChannel: VerificationChannel;
  nextVerificationCodeCallEpoch: number | null;
  email?: string | null;
  setMode: (mode: Mode) => void;
  setEmail: (email: string | null) => void;
  setVerificationChannel: (verificationChannel: VerificationChannel) => void;
  sendVerificationCode: () => Promise<void>;
  changeVerificationMethod: (
    newVerificationChannel: VerificationChannel,
    newEmail?: string | null,
  ) => void;
};

const useTwoFAAuth = ({ phoneNumber }: UseTwoFAAuthProps): UseTwoFAAuthReturn => {
  const [mode, setMode] = useState<Mode>('verifyCode');
  const [email, setEmail] = useState<string | null>(null);
  const [verificationChannel, setVerificationChannel] = useState<VerificationChannel>(
    VERIFICATION_CHANNEL.SMS,
  );
  const [nextVerificationCodeCallEpoch, setNextVerificationCodeCallEpoch] = useState<
    number | null
  >(null);

  const consumer = useMemo(() => {
    switch (verificationChannel) {
      case VERIFICATION_CHANNEL.EMAIL:
        if (!email) {
          throw new Error('Email is not defined, but verification channel is email');
        }
        return email;
      case VERIFICATION_CHANNEL.SMS:
        return phoneNumber;
    }
  }, [verificationChannel, email, phoneNumber]);

  const sendVerificationCode = async () => {
    const { nextCallEpoch } = await twoFAService.sendVerificationCode(
      consumer,
      verificationChannel,
    );

    setNextVerificationCodeCallEpoch(nextCallEpoch);
  };

  const changeVerificationMethod = (
    newVerificationChannel: VerificationChannel,
    newEmail?: string | null,
  ) => {
    setVerificationChannel(newVerificationChannel);
    if (newEmail) {
      setEmail(newEmail);
    }
    setMode('verifyCode');
  };

  useEffect(() => {
    sendVerificationCode();
  }, [consumer]);

  return {
    mode,
    consumer,
    nextVerificationCodeCallEpoch,
    email,
    verificationChannel,
    setMode,
    setEmail,
    setVerificationChannel,
    sendVerificationCode,
    changeVerificationMethod,
  };
};

export { useTwoFAAuth };
